import { api } from "@/lib/api";

/**
 * Tracks posts this browser submitted that are still awaiting approval,
 * so the author can see their own post instead of wondering whether it
 * saved. Stored locally — nobody else sees these.
 */

const KEY = "nc_pending_posts";
const MAX_AGE_DAYS = 14;

function read() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "[]");
    if (!Array.isArray(raw)) return [];
    const cutoff = Date.now() - MAX_AGE_DAYS * 864e5;
    return raw.filter(p => p && p.id && (p.savedAt || 0) > cutoff);
  } catch {
    return [];
  }
}

function write(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, 20)));
  } catch {
    /* private mode or quota — the feature just degrades to nothing */
  }
}

export function getPendingPosts() {
  return read();
}

export function addPendingPost(post) {
  if (!post?.id) return;
  const list = read().filter(p => p.id !== post.id);
  list.unshift({
    id: post.id,
    title: post.title,
    body: post.body || post.content || "",
    name: post.name || post.author || "You",
    city: post.city || "",
    cat: post.cat || post.topic || "General",
    created_at: post.created_at || new Date().toISOString(),
    savedAt: Date.now(),
  });
  write(list);
}

export function removePendingPost(id) {
  write(read().filter(p => p.id !== id));
}

/**
 * Ask the server which of our tracked posts are still pending.
 * Anything now live (or deleted) is dropped from local storage.
 * Returns the ones still awaiting review.
 */
export async function reconcilePending() {
  const list = read();
  if (list.length === 0) return [];

  try {
    const { data } = await api.get("/posts/status", {
      params: { ids: list.map(p => p.id).join(",") },
    });
    const stillPending = list.filter(p => data[p.id] === "pending");
    if (stillPending.length !== list.length) write(stillPending);
    return stillPending;
  } catch {
    // Offline or API hiccup — show what we have rather than hiding it
    return list;
  }
}
